"use client";

import React, { useEffect, useState, useMemo } from "react";
import Image from "next/image";
import { MotionDiv, MotionSection, MotionForm, Variants } from "@/utils/clientMotion";

interface LocationApiCampaign { name?: string; }
interface LocationApiRecord {
  name?: string;
  campaigns?: LocationApiCampaign[];
  location?: { name?: string } | null;
}
interface LocationApiResponse { total: number; data: LocationApiRecord[]; }
interface Counts { [name: string]: number; }
const LOCATION_API_ENDPOINT = "/api/rebott-locations";

// Helpers
function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ตำแหน่งสำหรับ Desktop (md+)
const POS_DESKTOP = [
  { top: "50%", left: "40%" },
  { top: "35%", left: "60%" },
  { top: "45%", left: "25%" },
  { top: "30%", left: "70%" },
  { top: "60%", left: "55%" },
  { top: "65%", left: "75%" },
];

// ตำแหน่งสำหรับ Mobile
const POS_MOBILE = [
  { top: "55%", left: "20%" }, // Sun Tower
  { top: "45%", left: "45%" }, // RVM-all Thailand golf tour
  { top: "40%", left: "75%" }, // RVM-BG Stadium
  { top: "65%", left: "35%" }, // ICT
  { top: "75%", left: "60%" }, // RVM-location
  { top: "60%", left: "85%" }, // RVM Central Plaza — ขยับลงมาอีกนิดจาก 50% → 70%
];

export default function RebottLocationsIntro() {
  const [counts, setCounts] = useState<Counts>({});
  const [isMobile, setIsMobile] = useState(false);

  // Fetch data
  useEffect(() => {
    async function fetchLocations() {
      try {
        const url = new URL(LOCATION_API_ENDPOINT, window.location.origin);
        url.searchParams.set("pageNumber", "1");
        url.searchParams.set("pageSize", "1000");

        const response = await fetch(url.toString());
        if (!response.ok) {
          setCounts({});
          return;
        }
        const data = (await response.json()) as LocationApiResponse;

        const cnts: Counts = {};
        const records = Array.isArray(data?.data) ? data.data : [];
        records.forEach((it) => {
          const displayName = (it.location?.name || it.name || "").trim();
          if (!displayName) return;

          // นับจำนวนโดยอิงจำนวน campaigns เพื่อให้พฤติกรรมใกล้เคียงหน้าเดิม
          const count = Math.max(it.campaigns?.length || 0, 1);
          cnts[displayName] = (cnts[displayName] || 0) + count;
        });

        setCounts(cnts);
      } catch {
        setCounts({});
      }
    }

    fetchLocations();
  }, []);

  // Detect mobile vs desktop
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const entries = Object.entries(counts);
  const maxCount = entries.length
    ? Math.max(...entries.map(([, c]) => c))
    : 1;

  const colors = useMemo(() => {
    const base = entries.map((_, i) => (i % 2 === 0 ? "#72c8f8" : "#8ed46e"));
    return shuffle(base);
  }, [entries]);

  // Component for Desktop bubbles
  function DesktopBubbles() {
    return (
      <>
        {entries.map(([name, count], idx) => {
          const size = 50 + (count / maxCount) * 100;
          const { top, left } = POS_DESKTOP[idx % POS_DESKTOP.length];
          const bgColor = colors[idx];

          return (
            <MotionDiv
              key={name}
              initial={{ y: -200, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{
                delay: idx * 0.15,
                type: "spring",
                stiffness: 100,
              }}
              className="absolute flex flex-col items-center justify-center text-white font-semibold p-3"
              style={{
                top,
                left,
                width: size,
                height: size,
                marginLeft: -size / 2,
                marginTop: -size / 2,
                borderRadius: "50%",
                backgroundColor: bgColor,
              }}
            >
              <div className="text-xs text-center leading-tight px-1">
                {name}
              </div>
              <div className="text-lg">{count}</div>
            </MotionDiv>
          );
        })}
      </>
    );
  }

  // Component for Mobile bubbles
  function MobileBubbles() {
    return (
      <>
        {entries.map(([name, count], idx) => {
          const baseSize = 50 + (count / maxCount) * 100;
          const size = baseSize * 0.7;
          const { top, left } = POS_MOBILE[idx % POS_MOBILE.length];
          const bgColor = colors[idx];

          return (
            <MotionDiv
              key={name}
              initial={{ y: -200, opacity: 0 }}
              viewport={{ once: false, amount: 0.1 }} 
              animate={{ y: 0, opacity: 1 }}
              transition={{
                delay: idx * 0.1,
                type: "spring",
                stiffness: 100,
              }}
              className="absolute flex flex-col items-center justify-center text-white font-semibold p-3"
              style={{
                top,
                left,
                width: size,
                height: size,
                marginLeft: -size / 2,
                marginTop: -size / 2,
                borderRadius: "50%",
                backgroundColor: bgColor,
              }}
            >
              <div className="text-[10px] text-center leading-tight px-1">
                {name}
              </div>
              <div className="text-sm">{count}</div>
            </MotionDiv>
          );
        })}
      </>
    );
  }

  return (
    <div className="relative w-full">
      <div className="relative w-full h-[400px] md:h-[600px]">
        <Image
          src="/image/robott_locations_intro.webp"
          alt="Intro background"
          fill
          className="object-cover"
        />

        {/* แยกเรนเดอร์ Mobile vs Desktop */}
        {isMobile ? <MobileBubbles /> : <DesktopBubbles />}
      </div>
    </div>
  );
}

